/**
 * RCD utility formatters — SSR-safe, no `any`.
 * Dates are ISO8601 YYYY-MM-DD in inbound data; output is a consistent
 * short format used by every RCD surface (and the detail stage ribbon).
 */

/** Formats a currency value in USD with no decimals for whole-dollar display. */
export function formatMoney(n: number): string {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Formats an ISO date (YYYY-MM-DD) as `MMM D, YYYY` (e.g. "Apr 21, 2026"). */
export function formatShortDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Difference in whole days between the target ISO date and today. Negative = past. */
export function dayDiff(iso: string, now: Date = new Date()): number {
  const target = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(target.getTime())) return 0;
  const ms = target.getTime() - new Date(now.toISOString().slice(0, 10) + 'T00:00:00').getTime();
  return Math.round(ms / 86_400_000);
}

/** True if the target date is within `window` days in the future (inclusive). */
export function isWithinDays(iso: string, window: number, now: Date = new Date()): boolean {
  const d = dayDiff(iso, now);
  return d >= 0 && d <= window;
}

/** Formats a 9-digit CIF as `CIF-XXXX` style — non-destructive if already prefixed. */
export function formatCifId(cifId: string): string {
  if (cifId.startsWith('CIF-')) return cifId;
  return `CIF-${cifId}`;
}
